import { useCallback, useEffect, useRef, useState } from "react";
import OffersService from "../Services/OffersService";
import { errorNotification } from "../../Generic/Notifications";
import { useOfferContext } from "../context/OfferContext";
import { buildOffersFilterKey } from "../utils/offersListFilters";

function isAborted(signal, response) {
	return Boolean(signal?.aborted || response?.aborted);
}

function replaceActiveRequest(activeRequestRef) {
	if (activeRequestRef.current) {
		activeRequestRef.current.abort();
	}
	const controller = new AbortController();
	activeRequestRef.current = controller;
	return controller;
}

async function fetchOffersPage({ query, restaurantId, page, signal }) {
	if (query) {
		return OffersService.searchOffers({ query, page, restaurantId, signal });
	}
	return OffersService.getOffers({ page, restaurantId, signal });
}

function readOffersPage(response) {
	if (response?.aborted) {
		return { aborted: true };
	}
	if (response?.error) {
		return { error: response.error };
	}
	const data = response?.data;
	return {
		items: data?.items || [],
		page: data?.page,
		hasNext: data?.hasNextPage || false,
	};
}

/**
 * Loads and paginates the offers list for the current URL filters.
 * Keeps rows mounted across filter changes until scroll-to-top and page 1 are ready.
 */
export function useOffersList({ query, restaurantId, listAnchorRef }) {
	const {
		offers,
		offersFilterKey,
		currentPage,
		hasNextPage,
		lastClickedOfferId,
		setOffersList,
		appendOffersList,
		setLastClickedOfferId,
	} = useOfferContext();

	const filterKey = buildOffersFilterKey(query, restaurantId);
	const cacheHit = offersFilterKey === filterKey;

	const [initialLoading, setInitialLoading] = useState(!cacheHit);
	const [filterTransitioning, setFilterTransitioning] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [initialError, setInitialError] = useState(false);
	const [loadMoreError, setLoadMoreError] = useState(false);

	const loadMoreLockRef = useRef(false);
	const activeRequestRef = useRef(null);
	const resetInFlightRef = useRef(false);

	const markFilterResetStarted = useCallback(
		(hadRows) => {
			setFilterTransitioning(hadRows);
			setLoadMoreError(false);
			setLoadingMore(false);
			loadMoreLockRef.current = false;
			if (!hadRows) {
				setInitialLoading(true);
			}
			setInitialError(false);
			setLastClickedOfferId(null);
		},
		[setLastClickedOfferId]
	);

	const finishFilterReset = useCallback(() => {
		resetInFlightRef.current = false;
		setInitialLoading(false);
		setFilterTransitioning(false);
	}, []);

	const applyFirstPage = useCallback(
		(nextFilterKey, pageResult) => {
			setOffersList(pageResult.items, pageResult.page, pageResult.hasNext, nextFilterKey);
			setInitialError(false);
			finishFilterReset();
		},
		[finishFilterReset, setOffersList]
	);

	const applyFirstPageError = useCallback(
		(nextFilterKey, error) => {
			errorNotification(error);
			setOffersList([], 1, false, nextFilterKey);
			setInitialError(true);
			finishFilterReset();
		},
		[finishFilterReset, setOffersList]
	);

	const resetForFilters = useCallback(
		async (nextQuery, nextRestaurantId) => {
			const nextFilterKey = buildOffersFilterKey(nextQuery, nextRestaurantId);
			const controller = replaceActiveRequest(activeRequestRef);
			resetInFlightRef.current = true;
			markFilterResetStarted(offers.length > 0);

			if (listAnchorRef?.current) {
				listAnchorRef.current.scrollTop = 0;
			}

			const fetchPromise = fetchOffersPage({
				query: nextQuery,
				restaurantId: nextRestaurantId,
				page: 1,
				signal: controller.signal,
			});

			if (isAborted(controller.signal)) {
				return;
			}

			const response = await fetchPromise;
			if (isAborted(controller.signal, response)) {
				return;
			}

			const pageResult = readOffersPage(response);
			if (pageResult.aborted) {
				return;
			}
			if (pageResult.error) {
				applyFirstPageError(nextFilterKey, pageResult.error);
				return;
			}

			applyFirstPage(nextFilterKey, {
				items: pageResult.items,
				page: pageResult.page || 1,
				hasNext: pageResult.hasNext,
			});
		},
		[applyFirstPage, applyFirstPageError, listAnchorRef, markFilterResetStarted, offers.length]
	);

	const loadMoreIsBlocked = useCallback(
		({ retry } = {}) => {
			if (resetInFlightRef.current) return true;
			if (loadMoreLockRef.current) return true;
			if (loadingMore) return true;
			if (!retry && loadMoreError) return true;
			if (!hasNextPage) return true;
			return false;
		},
		[hasNextPage, loadingMore, loadMoreError]
	);

	const releaseLoadMore = useCallback(() => {
		loadMoreLockRef.current = false;
		setLoadingMore(false);
	}, []);

	const applyNextPage = useCallback(
		({ filterKey: nextFilterKey, page, pageResult }) => {
			appendOffersList(
				pageResult.items,
				pageResult.page || page,
				pageResult.hasNext,
				nextFilterKey
			);
			setLoadMoreError(false);
			releaseLoadMore();
		},
		[appendOffersList, releaseLoadMore]
	);

	const fetchMore = useCallback(
		async (page, { retry = false } = {}) => {
			if (loadMoreIsBlocked({ retry })) return;

			const requestQuery = query;
			const requestRid = restaurantId;
			const requestFilterKey = buildOffersFilterKey(requestQuery, requestRid);
			const controller = replaceActiveRequest(activeRequestRef);

			loadMoreLockRef.current = true;
			setLoadingMore(true);
			setLoadMoreError(false);

			const response = await fetchOffersPage({
				query: requestQuery,
				restaurantId: requestRid,
				page,
				signal: controller.signal,
			});

			if (isAborted(controller.signal, response)) {
				releaseLoadMore();
				return;
			}

			const pageResult = readOffersPage(response);
			if (pageResult.aborted) {
				releaseLoadMore();
				return;
			}
			if (pageResult.error) {
				errorNotification(pageResult.error);
				setLoadMoreError(true);
				releaseLoadMore();
				return;
			}

			applyNextPage({
				filterKey: requestFilterKey,
				page,
				pageResult,
			});
		},
		[applyNextPage, loadMoreIsBlocked, query, releaseLoadMore, restaurantId]
	);

	const retryInitial = useCallback(() => {
		resetForFilters(query, restaurantId);
	}, [query, restaurantId, resetForFilters]);

	const retryLoadMore = useCallback(
		(page) => {
			setLoadMoreError(false);
			fetchMore(page, { retry: true });
		},
		[fetchMore]
	);

	useEffect(() => {
		if (offersFilterKey === filterKey) {
			setInitialLoading(false);
			setFilterTransitioning(false);
			return undefined;
		}
		resetForFilters(query, restaurantId);
	}, [filterKey, offersFilterKey, query, restaurantId, resetForFilters]);

	useEffect(() => {
		return () => {
			if (activeRequestRef.current) {
				activeRequestRef.current.abort();
			}
		};
	}, []);

	return {
		offers,
		currentPage,
		hasNextPage,
		lastClickedOfferId,
		setLastClickedOfferId,
		initialLoading: initialLoading && offers.length === 0,
		filterTransitioning,
		loadingMore,
		initialError,
		loadMoreError,
		fetchMore,
		retryInitial,
		retryLoadMore,
	};
}
